import { createSlice } from '@reduxjs/toolkit'
import {
  addAudit,
  calculateAuditActionsCosts,
  deleteAudit,
  getAuditByActionId,
  getAuditbyId,
  getAudits,
  getAuditsByMissionId,
  getCorrectiveActionPrioritiesForAudit,
  getCorrectiveActionStatusForAudit,
  removeAuditsFromAction,
  updateAudit,
} from './auditActions'
import { Audit, AuditResponse } from '../../types/auditTypes'

interface AuditState {
  audits: Audit[]
  loading: boolean
  audit: Audit | null
  error: string | null
  addedAudit: Audit | null
  updatedAudit: Audit | null
  AuditResponse: AuditResponse | null
  sourceAudits: Audit[]
  totalCost: number | null
  priorityCount: {
    CRITICAL: number
    HIGH: number
    MEDIUM: number
    LOW: number
  } | null
  statusCount: {
    WAITING: number
    IN_PROGRESS: number
    TO_DO: number
    SUSPENDED: number
    DONE: number
  } | null
}

const initialState: AuditState = {
  audits: [],
  loading: false,
  error: null,
  addedAudit: null,
  audit: null,
  updatedAudit: null,
  AuditResponse: null,
  sourceAudits: [],
  totalCost: null,
  priorityCount: null,
  statusCount: null,
}

export const auditSlice = createSlice({
  name: 'audit',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAudits.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAudits.fulfilled, (state, action) => {
        state.loading = false
        state.AuditResponse = {
          audits: action.payload.audits,
          totalPages: action.payload.totalPages ?? 0,
          total: action.payload.total ?? 0,
        }
      })
      .addCase(getAudits.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(deleteAudit.fulfilled, (state, action) => {
        state.loading = false
        const deletedAuditId = action.meta.arg

        if (state.AuditResponse) {
          state.AuditResponse.audits = (
            state.AuditResponse.audits ?? []
          ).filter((audit) => audit._id !== deletedAuditId)

          state.AuditResponse.total = state.AuditResponse.total ?? 0
          state.AuditResponse.totalPages = state.AuditResponse.totalPages ?? 0
          state.AuditResponse.currentPage = state.AuditResponse.currentPage ?? 0
        }

        if (state.audits) {
          state.audits = (state.audits ?? []).filter(
            (audit) => audit._id !== deletedAuditId
          )
        }
      })
      .addCase(addAudit.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addAudit.fulfilled, (state, action) => {
        state.loading = false
        state.addedAudit = action.payload
        state.audits.push(action.payload)
      })
      .addCase(addAudit.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(updateAudit.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateAudit.fulfilled, (state, action) => {
        state.loading = false
        state.updatedAudit = action.payload
      })
      .addCase(updateAudit.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getAuditbyId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAuditbyId.fulfilled, (state, action) => {
        state.loading = false
        state.audit = action.payload
      })
      .addCase(getAuditbyId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getAuditsByMissionId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAuditsByMissionId.fulfilled, (state, action) => {
        state.loading = false
        const audits = Object.values(action.payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.audits = audits as []
      })
      .addCase(getAuditsByMissionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })
      .addCase(getAuditByActionId.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAuditByActionId.fulfilled, (state, action) => {
        const payload = action.payload
        const audits = Object.values(payload).filter(
          (item) => item && typeof item === 'object'
        )
        state.loading = false
        state.sourceAudits = audits as []
      })
      .addCase(getAuditByActionId.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeAuditsFromAction.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Something went wrong'
      })

      .addCase(removeAuditsFromAction.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeAuditsFromAction.fulfilled, (state, action) => {
        state.loading = false
        state.audits = action.payload
      })
      .addCase(calculateAuditActionsCosts.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(calculateAuditActionsCosts.fulfilled, (state, action) => {
        state.loading = false
        state.totalCost = action.payload.totalCost
      })
      .addCase(calculateAuditActionsCosts.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload || 'Failed to calculate audit actions costs.'
      })
      .addCase(getCorrectiveActionPrioritiesForAudit.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getCorrectiveActionPrioritiesForAudit.fulfilled,
        (state, action) => {
          state.loading = false
          state.priorityCount = action.payload.priorityCount
        }
      )
      .addCase(
        getCorrectiveActionPrioritiesForAudit.rejected,
        (state, action) => {
          state.loading = false
          state.error =
            action.payload || 'Failed to fetch corrective action priorities.'
        }
      )
      .addCase(getCorrectiveActionStatusForAudit.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getCorrectiveActionStatusForAudit.fulfilled, (state, action) => {
        state.loading = false
        state.statusCount = action.payload.statusCount
      })
      .addCase(getCorrectiveActionStatusForAudit.rejected, (state, action) => {
        state.loading = false
        state.error =
          action.payload || 'Failed to fetch corrective action statuses.'
      })
  },
})

export default auditSlice.reducer
